import { useChargebee } from 'client/modules/App/ChargebeeContext';
import { getToken } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { getI18n } from '../../../../../AppReducer';
import PricingPlan from '../../../../App/components/PricingPlan/PricingPlan';
import Spinner from '../../../../App/components/Spinner/Spinner';
import { fetchPlans } from '../../PricingActions';
import { getPlans } from '../../PricingReducer';
import { setError } from '../../../../App/AppActions';
import { FormattedMessage } from 'react-intl';

const PricingPlans = ({ period = '1m' }) => {
  const navigate = useNavigate();
  const token = useSelector(getToken);
  const dispatch = useDispatch();
  const cbInstance = useChargebee();
  const plans = useSelector(getPlans);
  const locale = useSelector(getI18n);

  useEffect(() => {
    if (!plans?.length) {
      dispatch(fetchPlans(period)); 
    }
  }, [plans, period]);

  const handleSelect = async plan => {
    if (!token) {
      return;
    }
    const response = await callApi(dispatch, 'users/checkout-pages', token, 'POST', {
      planId: plan.id,
      locale,
    }).catch((response) => {
      if (response.errors && response.errors.user && response.errors.user === 'had_subscription') {
        dispatch(setError(
          <FormattedMessage
            id="Subscription.HadSubscription"
            defaultMessage="You already had an active subscription. Go to {subscriptionSettings}."
            values={{
              subscriptionSettings: (
                <Link to="/settings/billing">
                  <FormattedMessage
                    id="Subscription.SubscriptionSettings"
                    defaultMessage="subscription settings"
                  />
                </Link>
              ),
            }}
          />,
        ));
      } else {
        dispatch(setError(
          <FormattedMessage
            id="Subscription.CreationError"
            defaultMessage="There was an error creating subscription, please try again."
          />,
        ));
      }
    });
    const { url, id: hostedPageId } = response.hosted_page;
    cbInstance.openCheckout({
      hostedPage: async () => ({
        url,
        embed: true,
        type: 'checkout_new',
      }),

      /*
       * This will be triggered when checkout is complete.
       */
      success() {
        cbInstance.closeAll();
        navigate(`/subscriptions/ok?state=succeeded&id=${hostedPageId}`);
      },
    });
  };

  if (!plans || plans.length === 0) {
    return <Spinner />;
  }

  return (
    <>
      {plans.map(plan => (
        <PricingPlan
          key={plan.id}
          name={plan.name}
          price={plan.price / 100}
          currency={plan.currency_code}
          people={plan.cf_people}
          popular={plan.cf_featured}
          freeMonths={plan.cf_free_months || 0}
          period={plan.period}
          unit={plan.period_unit}
          handleSelect={handleSelect.bind(null, plan)}
        />
      ))}
    </>
  );
};

PricingPlans.propTypes = {
  period: PropTypes.oneOf(['1m', '6m', '1y']),
};

export default PricingPlans;
